/**
 * ActiveJobsEta — "¿qué está corriendo y cuándo termina?".
 *
 * Tile del Dashboard para el industrialist con jobs de manufactura/reacción
 * activos. Muestra los top N jobs (sorted by ETA ascending — el que
 * termina primero va primero) con:
 *   - Progress bar (0-1 fraction del ESI)
 *   - Live ticking countdown (HH:MM:SS / "Dd Hh" / "listo") — se re-renderiza
 *     cada 1s via setInterval, así el widget SIEMPRE muestra el tiempo real
 *     restante sin tener que recargar la página.
 *   - Sub-line: runs + ends at HH:MM UTC + margin (si hay)
 *
 * Data: reutiliza `api.industryValuation()` — el endpoint ya enriquece cada
 * job con `remaining_seconds`, `progress`, `margin_total`, `status`,
 * `blueprint_name`, `product_name`, `runs`, `end_date`. Cero backend change.
 *
 * Patrón UX:
 *   - Si `status === 'ready'` → "ready to deliver" badge + timestamp del
 *     end_date (no countdown). Caso común: job de T2 que termina overnight
 *     y el industrialist lo entrega al día siguiente.
 *   - Si `status === 'paused'` → "pausado" badge, countdown congelado
 *     (no se mueve). Útil si el user pausa para liberar slot.
 *   - Si `status === 'active'` → live ticking countdown.
 *   - Job de research (margin_* null) → mostramos "—" en margin y un hint
 *     "research job" en italic. No esconde la fila — el user igual quiere
 *     ver ETA de su ME/TE/copy/invention.
 *
 * Failure:
 *   - isError → banner rojo (el dashboard sigue funcionando).
 *   - Sin active jobs → "no jobs running" con link a /industry para
 *     empezar uno.
 *
 * Caveat de tiempo:
 *   - ETA se calcula con `new Date(end_date).getTime() - now`. ESI puede
 *     shift end_date si el server reinicia el job (industry downtime).
 *     Cuando el user hace refetch y obtiene un end_date nuevo, el widget
 *     se actualiza solo.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, formatISKShort } from '../api'

interface ActiveJob {
  job_id: number
  activity_name: string
  blueprint_name: string
  product_name: string | null
  runs: number
  status: 'active' | 'paused' | 'ready' | 'delivered' | 'cancelled' | 'reverted'
  start_date: string
  end_date: string
  progress: number
  remaining_seconds: number
  margin_total: number | null
  margin_per_hour: number | null
}

const TOP_N = 5

export function ActiveJobsEta() {
  const { data, isLoading, isError, error, dataUpdatedAt } = useQuery({
    queryKey: ['industry-valuation', 'active-jobs-widget'],
    queryFn: () => api.industryValuation(false, false, 'fuzzwork'),
    // KV cache del endpoint maneja staleness. Refresca en focus.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })

  // Live ticker — `now` se actualiza cada 1s. Componente chico, re-render
  // total está OK (TanStack Query cache no se invalida, sólo cambia el texto).
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  if (isError) {
    return (
      <div className="bg-eve-danger/10 border border-eve-danger/30 rounded-md p-3 text-eve-danger text-sm">
        Error cargando jobs activos: {(error as Error).message}
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="bg-eve-panel border border-eve-border rounded-md p-5 space-y-3">
        <div className="h-3 w-1/3 bg-eve-border/40 rounded animate-pulse" />
        <div className="h-4 bg-eve-border/30 rounded animate-pulse" />
        <div className="h-4 bg-eve-border/30 rounded animate-pulse" />
        <div className="h-4 bg-eve-border/30 rounded animate-pulse w-5/6" />
      </div>
    )
  }

  // Top N jobs activos/ready/paused, sorted por end_date ASC (termina
  // primero = top). Excluimos delivered/cancelled/reverted (esos son
  // historial, ya están en /industry).
  const liveJobs = data.jobs
    .filter((j) =>
      j.status === 'active' || j.status === 'paused' || j.status === 'ready',
    )
    .sort(
      (a, b) => new Date(a.end_date).getTime() - new Date(b.end_date).getTime(),
    )
    .slice(0, TOP_N)

  return (
    <div className="bg-gradient-to-br from-eve-panel to-eve-bg border border-eve-border rounded-md p-5">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-eve-muted text-[10px] uppercase tracking-widest font-medium">
            Active jobs
          </div>
          <div className="font-display text-xl font-semibold text-eve-text mt-0.5">
            {data.summary.total_active_count}{' '}
            {data.summary.total_active_count === 1 ? 'corriendo' : 'corriendo'}
            {data.summary.profitable_jobs > 0 && (
              <span className="text-sm text-eve-muted ml-2 font-sans">
                · {data.summary.profitable_jobs} profitable
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-eve-muted-2 font-mono">
          <Link
            to="/industry"
            className="text-eve-accent hover:underline underline-offset-2"
          >
            ver todos →
          </Link>
          <span className="text-eve-muted-2">·</span>
          <span className="italic">
            ref {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : '—'} UTC
          </span>
        </div>
      </div>

      {liveJobs.length === 0 ? (
        <div className="mt-4 text-eve-muted text-sm italic">
          Sin jobs activos. <Link to="/industry" className="text-eve-accent hover:underline">Arrancá uno</Link>.
        </div>
      ) : (
        <ul className="mt-4 space-y-3">
          {liveJobs.map((j) => (
            <JobRow key={j.job_id} job={j} now={now} />
          ))}
        </ul>
      )}
    </div>
  )
}

function JobRow({ job, now }: { job: ActiveJob; now: number }) {
  const endMs = new Date(job.end_date).getTime()
  const remainingMs = endMs - now
  const readyOrPast = job.status === 'ready' || remainingMs <= 0
  const paused = job.status === 'paused'

  return (
    <li>
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-medium text-eve-text truncate">
              {job.blueprint_name}
            </span>
            {job.product_name && (
              <span className="text-xs text-eve-muted truncate">
                · {job.product_name}
              </span>
            )}
            <span className="text-xs text-eve-muted-2">
              · {job.runs} {job.runs === 1 ? 'run' : 'runs'}
            </span>
          </div>
          <ProgressBar progress={job.progress} paused={paused} ready={readyOrPast} />
        </div>
        <div className="text-right shrink-0">
          {readyOrPast ? (
            <ReadyBadge endMs={endMs} />
          ) : paused ? (
            <PausedBadge />
          ) : (
            <CountdownLabel remainingMs={remainingMs} />
          )}
          <div className="text-[10px] text-eve-muted-2 mt-0.5 font-mono italic">
            ends {formatTime(endMs)} UTC
          </div>
        </div>
      </div>
      <div className="mt-0.5 text-[11px] text-eve-muted font-mono flex items-center gap-2 flex-wrap">
        <span>{Math.round(job.progress * 100)}% complete</span>
        <span className="text-eve-muted-2">·</span>
        {job.margin_total !== null ? (
          <>
            <span>
              margin{' '}
              <span
                className={job.margin_total >= 0 ? 'text-eve-success' : 'text-eve-danger'}
              >
                {job.margin_total >= 0 ? '+' : '−'}
                {formatISKShort(Math.abs(job.margin_total))} ISK
              </span>
            </span>
            {job.margin_per_hour !== null && (
              <>
                <span className="text-eve-muted-2">·</span>
                <span>
                  {formatISKShort(job.margin_per_hour)} ISK/h
                </span>
              </>
            )}
          </>
        ) : (
          <span className="italic">research job</span>
        )}
      </div>
    </li>
  )
}

function ProgressBar({
  progress,
  paused,
  ready,
}: {
  progress: number
  paused: boolean
  ready: boolean
}) {
  const pct = Math.max(0, Math.min(1, progress)) * 100
  const fillColor = ready
    ? 'bg-eve-success'
    : paused
    ? 'bg-eve-muted'
    : 'bg-eve-accent'
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      className="mt-1 h-1.5 bg-eve-border/50 rounded-full overflow-hidden"
    >
      <div
        className={`h-full ${fillColor} transition-all duration-700 ease-out`}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

function CountdownLabel({ remainingMs }: { remainingMs: number }) {
  return (
    <span className="font-mono text-sm font-semibold text-eve-accent tabular-nums">
      {formatRemaining(remainingMs)}
    </span>
  )
}

function ReadyBadge({ endMs }: { endMs: number }) {
  return (
    <div className="flex flex-col items-end">
      <span className="inline-block bg-eve-success/15 border border-eve-success/40 text-eve-success text-[10px] uppercase tracking-wider font-semibold rounded px-2 py-0.5">
        ready
      </span>
      <span className="text-[10px] text-eve-muted-2 mt-0.5 font-mono italic">
        terminado {new Date(endMs).toLocaleDateString()}
      </span>
    </div>
  )
}

function PausedBadge() {
  return (
    <span className="inline-block bg-eve-muted/15 border border-eve-muted/40 text-eve-muted text-[10px] uppercase tracking-wider font-semibold rounded px-2 py-0.5">
      pausado
    </span>
  )
}

// --- helpers ---

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'listo'
  const totalSec = Math.floor(ms / 1000)
  const days = Math.floor(totalSec / 86_400)
  const hours = Math.floor((totalSec % 86_400) / 3_600)
  const minutes = Math.floor((totalSec % 3_600) / 60)
  const seconds = totalSec % 60
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${pad(minutes)}m`
  if (minutes > 0) return `${minutes}m ${pad(seconds)}s`
  return `${seconds}s`
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })
}
